import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Save, Eye, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ArticleEditor from "@/components/admin/ArticleEditor";

const EMPTY = {
  title: "", slug: "", excerpt: "", featured_image: "",
  author_id: "peps4pets-research", category_id: "", tag_ids: [],
  related_products: [], related_articles: [], status: "draft",
  publish_at: "", difficulty: "Beginner",
  content_html: "", content_json: null,
  references: [], faqs: [], sections: null,
  seo_title: "", meta_description: "", canonical_url: "", focus_keyword: "",
  secondary_keywords: [], og_title: "", og_description: "",
  twitter_card: "summary_large_image", schema_type: "Article", template_key: "",
};

export default function AdminArticleEditor() {
  const { slug } = useParams();
  const isNew = !slug || slug === "new";
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [cats, setCats] = useState([]);
  const [tags, setTags] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [products, setProducts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/article-categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/admin/article-tags").then((r) => setTags(r.data)).catch(() => {});
    api.get("/admin/article-authors").then((r) => setAuthors(r.data)).catch(() => {});
    api.get("/admin/article-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/articles").then((r) => setArticles(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isNew) return;
    api.get(`/admin/articles/${slug}`).then((r) => setForm({ ...EMPTY, ...r.data })).catch(() => toast.error("Article not found"));
  }, [slug, isNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyTemplate = (key) => {
    const t = templates.find((x) => x.key === key);
    if (!t) return;
    if (form.content_html && !window.confirm("Replace current content with template?")) return;
    setForm((f) => ({ ...f, template_key: key, content_html: t.content_html, sections: t.sections || null }));
  };

  const wordCount = useMemo(() => (form.content_html || "").replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length, [form.content_html]);
  const readingTime = Math.max(1, Math.ceil(wordCount / 220));

  const toggleArrayItem = (key, val) => {
    setForm((f) => ({ ...f, [key]: (f[key] || []).includes(val) ? f[key].filter((x) => x !== val) : [...(f[key] || []), val] }));
  };

  const save = async (newStatus) => {
    setSaving(true);
    try {
      const body = { ...form };
      if (newStatus) body.status = newStatus;
      if (!body.slug && body.title) body.slug = body.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 100);
      let res;
      if (isNew) res = await api.post("/admin/articles", body);
      else res = await api.patch(`/admin/articles/${slug}`, body);
      toast.success("Saved");
      setForm({ ...EMPTY, ...res.data });
      if (isNew) nav(`/admin/research/articles/${res.data.slug}`, { replace: true });
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  return (
    <div data-testid="admin-article-editor" className="max-w-6xl">
      <Link to="/admin/research/articles" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft className="w-4 h-4"/> Articles</Link>

      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow mb-2">Research Hub · Article Editor</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">{isNew ? "New article" : form.title || "Untitled"}</h1>
          <div className="text-xs text-slate-500 mt-1">{wordCount} words · {readingTime} min read</div>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {form.slug && form.status === "published" && (
            <a href={`/research/${form.slug}`} target="_blank" rel="noreferrer">
              <Button variant="outline" className="border-slate-300"><Eye className="w-4 h-4 mr-1.5"/> Preview</Button>
            </a>
          )}
          <Button onClick={() => save("draft")} disabled={saving} variant="outline" data-testid="save-draft-btn" className="border-slate-300"><Save className="w-4 h-4 mr-1.5"/> Save draft</Button>
          <Button onClick={() => save("published")} disabled={saving} data-testid="publish-btn" className="bg-blue-600 hover:bg-blue-700 text-white">Publish</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-4">
          {/* Title / slug */}
          <div className="border border-slate-200 rounded-md bg-white p-5 space-y-3">
            <Input placeholder="Article title" value={form.title} onChange={(e) => set("title", e.target.value)} className="text-2xl font-display font-semibold h-12" data-testid="art-title"/>
            <Input placeholder="URL slug (leave empty to auto-generate)" value={form.slug} onChange={(e) => set("slug", e.target.value)} className="font-mono text-sm" data-testid="art-slug" disabled={!isNew}/>
            <Textarea rows={2} placeholder="Excerpt (shown in listings)" value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} data-testid="art-excerpt"/>
            <Input placeholder="Featured image URL" value={form.featured_image} onChange={(e) => set("featured_image", e.target.value)} data-testid="art-featured-image"/>
          </div>

          {/* Editor */}
          <ArticleEditor
            value={{ html: form.content_html, json: form.content_json }}
            onChange={({ html, json }) => setForm((f) => ({ ...f, content_html: html, content_json: json }))}
          />

          {/* References */}
          <div className="border border-slate-200 rounded-md bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-lg font-semibold">References</div>
              <Button size="sm" onClick={() => set("references", [...(form.references || []), { title: "", authors: "", journal: "", year: "", doi: "", pubmed: "", url: "" }])} className="bg-slate-900 hover:bg-blue-600 text-white h-8 text-xs">Add reference</Button>
            </div>
            {(form.references || []).length === 0 ? <div className="text-xs text-slate-500">No references yet.</div> : (
              <div className="space-y-3">
                {(form.references || []).map((r, i) => (
                  <div key={i} className="grid grid-cols-6 gap-2 items-center border border-slate-200 rounded p-2">
                    <Input placeholder="Title" value={r.title} onChange={(e) => set("references", form.references.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} className="col-span-2 h-8 text-xs"/>
                    <Input placeholder="Authors" value={r.authors} onChange={(e) => set("references", form.references.map((x, j) => j === i ? { ...x, authors: e.target.value } : x))} className="h-8 text-xs"/>
                    <Input placeholder="Journal" value={r.journal} onChange={(e) => set("references", form.references.map((x, j) => j === i ? { ...x, journal: e.target.value } : x))} className="h-8 text-xs"/>
                    <Input placeholder="Year" value={r.year} onChange={(e) => set("references", form.references.map((x, j) => j === i ? { ...x, year: e.target.value } : x))} className="h-8 text-xs"/>
                    <div className="flex items-center gap-1">
                      <Input placeholder="DOI / URL" value={r.doi || r.url} onChange={(e) => set("references", form.references.map((x, j) => j === i ? { ...x, doi: e.target.value } : x))} className="h-8 text-xs"/>
                      <button onClick={() => set("references", form.references.filter((_, j) => j !== i))} className="p-1 rounded hover:bg-red-50 text-red-500"><Trash2 className="w-3.5 h-3.5"/></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* FAQs */}
          <div className="border border-slate-200 rounded-md bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-lg font-semibold">FAQs <span className="text-xs text-slate-500">(feeds FAQPage schema)</span></div>
              <Button size="sm" onClick={() => set("faqs", [...(form.faqs || []), { question: "", answer: "" }])} className="bg-slate-900 hover:bg-blue-600 text-white h-8 text-xs">Add FAQ</Button>
            </div>
            {(form.faqs || []).length === 0 ? <div className="text-xs text-slate-500">No FAQs yet.</div> : (
              <div className="space-y-3">
                {form.faqs.map((f, i) => (
                  <div key={i} className="border border-slate-200 rounded p-2 space-y-2">
                    <div className="flex items-center gap-2">
                      <Input placeholder="Question" value={f.question} onChange={(e) => set("faqs", form.faqs.map((x, j) => j === i ? { ...x, question: e.target.value } : x))} className="flex-1 h-8 text-xs"/>
                      <button onClick={() => set("faqs", form.faqs.filter((_, j) => j !== i))} className="p-1 rounded hover:bg-red-50 text-red-500"><Trash2 className="w-3.5 h-3.5"/></button>
                    </div>
                    <Textarea rows={2} placeholder="Answer" value={f.answer} onChange={(e) => set("faqs", form.faqs.map((x, j) => j === i ? { ...x, answer: e.target.value } : x))} className="text-xs"/>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* SEO */}
          <div className="border border-slate-200 rounded-md bg-white p-5 space-y-3">
            <div className="font-display text-lg font-semibold">SEO</div>
            <Input placeholder="SEO title (defaults to Article title)" value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} data-testid="seo-title"/>
            <Textarea rows={2} placeholder="Meta description (defaults to excerpt)" value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)} data-testid="seo-meta"/>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Canonical URL" value={form.canonical_url} onChange={(e) => set("canonical_url", e.target.value)}/>
              <Input placeholder="Focus keyword" value={form.focus_keyword} onChange={(e) => set("focus_keyword", e.target.value)}/>
              <Input placeholder="OG title" value={form.og_title} onChange={(e) => set("og_title", e.target.value)}/>
              <Input placeholder="OG description" value={form.og_description} onChange={(e) => set("og_description", e.target.value)}/>
              <select value={form.twitter_card} onChange={(e) => set("twitter_card", e.target.value)} className="h-10 px-3 rounded-md border border-slate-300 text-sm">
                <option value="summary_large_image">Twitter: summary_large_image</option>
                <option value="summary">Twitter: summary</option>
              </select>
              <select value={form.schema_type} onChange={(e) => set("schema_type", e.target.value)} className="h-10 px-3 rounded-md border border-slate-300 text-sm">
                <option>Article</option><option>NewsArticle</option><option>BlogPosting</option><option>ScholarlyArticle</option>
              </select>
            </div>
            <Input placeholder="Secondary keywords (comma-separated)" value={(form.secondary_keywords || []).join(", ")} onChange={(e) => set("secondary_keywords", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}/>
          </div>
        </div>

        <aside className="lg:sticky lg:top-4 lg:h-fit space-y-4">
          <div className="border border-slate-200 rounded-md bg-white p-5 space-y-3">
            <div className="font-display text-base font-semibold">Publish</div>
            <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="art-status" className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm">
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
            {form.status === "scheduled" && (
              <Input type="datetime-local" value={form.publish_at ? form.publish_at.slice(0, 16) : ""} onChange={(e) => set("publish_at", new Date(e.target.value).toISOString())} data-testid="art-publish-at"/>
            )}
            <select value={form.difficulty} onChange={(e) => set("difficulty", e.target.value)} className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm">
              <option>Beginner</option><option>Intermediate</option><option>Advanced</option>
            </select>
          </div>

          <div className="border border-slate-200 rounded-md bg-white p-5 space-y-2">
            <div className="font-display text-base font-semibold">Template</div>
            <select value={form.template_key} onChange={(e) => applyTemplate(e.target.value)} className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm">
              <option value="">— None —</option>
              {templates.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
            </select>
          </div>

          <div className="border border-slate-200 rounded-md bg-white p-5 space-y-2">
            <div className="font-display text-base font-semibold">Taxonomy</div>
            <select value={form.author_id} onChange={(e) => set("author_id", e.target.value)} className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm">
              <option value="">— Author —</option>
              {authors.map((a) => <option key={a.slug} value={a.slug}>{a.name}</option>)}
            </select>
            <select value={form.category_id} onChange={(e) => set("category_id", e.target.value)} className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm">
              <option value="">— Category —</option>
              {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Tags</div>
              <div className="max-h-32 overflow-y-auto border border-slate-200 rounded p-1">
                {tags.map((t) => (
                  <label key={t.slug} className="flex items-center gap-2 px-2 py-0.5 text-xs hover:bg-slate-50 rounded">
                    <input type="checkbox" checked={(form.tag_ids || []).includes(t.slug)} onChange={() => toggleArrayItem("tag_ids", t.slug)}/>
                    <span>{t.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="border border-slate-200 rounded-md bg-white p-5 space-y-2">
            <div className="font-display text-base font-semibold">Related products</div>
            <div className="max-h-32 overflow-y-auto border border-slate-200 rounded p-1">
              {products.map((p) => (
                <label key={p.slug} className="flex items-center gap-2 px-2 py-0.5 text-xs hover:bg-slate-50 rounded">
                  <input type="checkbox" checked={(form.related_products || []).includes(p.slug)} onChange={() => toggleArrayItem("related_products", p.slug)} data-testid={`rp-${p.slug}`}/>
                  <span className="font-mono text-slate-500">{p.slug}</span>
                  <span className="text-slate-700 truncate">· {p.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="border border-slate-200 rounded-md bg-white p-5 space-y-2">
            <div className="font-display text-base font-semibold">Related articles</div>
            <div className="max-h-32 overflow-y-auto border border-slate-200 rounded p-1">
              {articles.filter((a) => a.slug !== form.slug).map((a) => (
                <label key={a.slug} className="flex items-center gap-2 px-2 py-0.5 text-xs hover:bg-slate-50 rounded">
                  <input type="checkbox" checked={(form.related_articles || []).includes(a.slug)} onChange={() => toggleArrayItem("related_articles", a.slug)}/>
                  <span className="truncate">{a.title}</span>
                </label>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
